import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Warehouse } from 'lucide-react';
import inventoryService from '../services/inventoryService';

const StockByWarehouseModal = ({ productId, productName, isOpen, onClose }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['inventory-stock-by-warehouse', productId],
    queryFn: () => inventoryService.getStockByWarehouse(productId),
    enabled: isOpen && !!productId
  });

  if (!isOpen) return null;

  const stockData = data?.data;
  const warehouses = stockData?.armazens || [];
  const variantes = stockData?.variantes || [];

  const formatQuantity = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const grandTotal = warehouses.reduce((acc, w) => acc + (parseFloat(w.estoque_total) || 0), 0);
  const grandReserved = warehouses.reduce((acc, w) => acc + (parseFloat(w.estoque_reservado) || 0), 0);
  const grandAvailable = warehouses.reduce((acc, w) => acc + (parseFloat(w.estoque_disponivel) || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b bg-gray-50 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Warehouse className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  Estoque de "{productName}"
                </h2>
                <p className="text-sm text-gray-500">
                  {warehouses.length} armazém(ns) • {variantes.length} variante(s)
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
              Erro ao carregar estoque por armazém.
            </div>
          )}

          {stockData && warehouses.length === 0 && (
            <div className="text-center py-12">
              <Warehouse className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum estoque cadastrado para este produto</p>
            </div>
          )}

          {stockData && warehouses.length > 0 && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-blue-600">Total Geral</p>
                  <p className="text-2xl font-bold text-blue-900">{grandTotal.toLocaleString('pt-BR')}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-yellow-600">Reservado</p>
                  <p className="text-2xl font-bold text-yellow-900">{grandReserved.toLocaleString('pt-BR')}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-green-600">Disponível</p>
                  <p className="text-2xl font-bold text-green-900">{grandAvailable.toLocaleString('pt-BR')}</p>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Armazém
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reservado
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Disponível
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {warehouses.map((warehouse) => (
                      <tr key={warehouse.armazem_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-gray-900">
                              {warehouse.armazem_nome}
                            </div>
                            {warehouse.armazem_codigo && (
                              <span className="ml-2 text-xs text-gray-500">
                                ({warehouse.armazem_codigo})
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900 font-medium">
                          {formatQuantity(warehouse.estoque_total)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900">
                          {formatQuantity(warehouse.estoque_reservado)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <span className={`font-medium ${
                            parseFloat(warehouse.estoque_disponivel || 0) <= 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {formatQuantity(warehouse.estoque_disponivel)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-4 py-3 text-left text-sm font-bold text-gray-900">
                        TOTAL GERAL
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                        {formatQuantity(grandTotal)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                        {formatQuantity(grandReserved)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                        {formatQuantity(grandAvailable)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Variants Detail */}
              {variantes.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Detalhe por Variante</h3>
                  <div className="space-y-4">
                    {variantes.map((variant) => {
                      const variantTotal = variant.warehouses.reduce((acc, w) => acc + w.estoque_total, 0);
                      const variantReserved = variant.warehouses.reduce((acc, w) => acc + w.estoque_reservado, 0);
                      const variantAvailable = variant.warehouses.reduce((acc, w) => acc + w.estoque_disponivel, 0);

                      return (
                        <div key={variant.variante_id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {variant.sku_variacao || `Variante #${variant.variante_id}`}
                              </p>
                              {(variant.cor || variant.tamanho_label) && (
                                <p className="text-xs text-gray-500">
                                  {[variant.cor, variant.tamanho_label].filter(Boolean).join(' • ')}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900">
                                Total: {formatQuantity(variantTotal)}
                              </p>
                              <p className="text-xs text-green-600">
                                Disp: {formatQuantity(variantAvailable)}
                              </p>
                            </div>
                          </div>

                          {variant.warehouses.length > 0 && (
                            <table className="min-w-full divide-y divide-gray-100 text-sm">
                              <thead>
                                <tr>
                                  <th className="px-2 py-1 text-left text-xs text-gray-400">Armazém</th>
                                  <th className="px-2 py-1 text-right text-xs text-gray-400">Total</th>
                                  <th className="px-2 py-1 text-right text-xs text-gray-400">Reservado</th>
                                  <th className="px-2 py-1 text-right text-xs text-gray-400">Disp.</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {variant.warehouses.map((wh, idx) => (
                                  <tr key={idx}>
                                    <td className="px-2 py-1 text-gray-600">{wh.armazem_nome}</td>
                                    <td className="px-2 py-1 text-right text-gray-900">{formatQuantity(wh.estoque_total)}</td>
                                    <td className="px-2 py-1 text-right text-gray-600">{formatQuantity(wh.estoque_reservado)}</td>
                                    <td className="px-2 py-1 text-right text-green-600 font-medium">{formatQuantity(wh.estoque_disponivel)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockByWarehouseModal;
